/**
 * BrowseCivitAICheckpointsModal
 *
 * Modal for searching CivitAI checkpoints via the backend proxy.
 * Shows result cards with NSFW badge, license summary, and install button.
 * Install flow: license confirm checkbox -> POST install -> progress polling ->
 * close on success. RULE #5: never silently swallow install failures.
 *
 * Dark theme only (bg-black/30, text-white/70, border-white/10). RULE #13: mobile-first.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import type { CivitAISearchItem, InstallJobStatus } from '@/services/civitaiApi';
import {
  searchCivitAICheckpoints,
  installCheckpoint,
  getCheckpointInstallStatus,
} from '@/services/civitaiApi';

// ============================================================================
// Types
// ============================================================================

interface BrowseCivitAICheckpointsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful install with the new filename. */
  onInstalled: (filename: string, base_model: 'sd15' | 'sdxl', is_hyper: boolean) => void;
}

type InstallState =
  | { phase: 'idle' }
  | { phase: 'confirm'; item: CivitAISearchItem }
  | { phase: 'installing'; item: CivitAISearchItem; jobId: string; status: InstallJobStatus }
  | { phase: 'done'; filename: string }
  | { phase: 'error'; item: CivitAISearchItem; message: string };

// ============================================================================
// Helpers
// ============================================================================

function deriveBaseModel(rawBaseModel: string | undefined): 'sd15' | 'sdxl' {
  const lower = (rawBaseModel ?? '').toLowerCase();
  if (lower.includes('sdxl') || lower.includes('xl 1.0')) return 'sdxl';
  return 'sd15';
}

function deriveIsHyper(rawBaseModel: string | undefined): boolean {
  return (rawBaseModel ?? '').toLowerCase().includes('hyper');
}

function useDebounce(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

// ============================================================================
// Sub-components
// ============================================================================

interface ResultCardProps {
  item: CivitAISearchItem;
  onInstall: (item: CivitAISearchItem) => void;
}

function ResultCard({ item, onInstall }: ResultCardProps) {
  const { t } = useTranslation();
  return (
    <div className="bg-black/30 border border-white/10 rounded-xl overflow-hidden flex flex-col">
      {/* Thumbnail */}
      <div className="relative h-36 bg-black/40 flex-shrink-0">
        {item.thumbnail_url ? (
          <img
            src={item.thumbnail_url}
            alt={item.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/30 text-xs">
            No preview
          </div>
        )}
        {item.nsfw && (
          <span className="absolute top-2 left-2 bg-red-600/90 text-white text-xs font-bold px-2 py-0.5 rounded">
            {t('deforum.picker.checkpoint.nsfwBadge')}
          </span>
        )}
      </div>

      {/* Info */}
      <div className="p-3 flex flex-col gap-1 flex-1">
        <p className="text-white text-sm font-semibold leading-tight line-clamp-2">{item.name}</p>
        <p className="text-white/50 text-xs">{item.creator}</p>
        <div className="flex flex-wrap gap-1 mt-1">
          <span className="text-white/40 text-xs bg-white/5 px-2 py-0.5 rounded">
            {item.base_model}
          </span>
          <span className="text-white/40 text-xs bg-white/5 px-2 py-0.5 rounded">
            {item.size_mb} MB
          </span>
          {item.rating !== null && (
            <span className="text-yellow-400/80 text-xs bg-white/5 px-2 py-0.5 rounded">
              ★ {item.rating.toFixed(1)}
            </span>
          )}
        </div>
        <p className="text-white/40 text-xs mt-auto pt-1 truncate">
          {item.license_label} · {item.downloads_count.toLocaleString()} downloads
        </p>
      </div>

      {/* Actions */}
      <div className="px-3 pb-3 flex gap-2">
        <button
          type="button"
          onClick={() => onInstall(item)}
          className="flex-1 min-h-[44px] bg-purple-600/80 hover:bg-purple-500/90 text-white text-sm font-medium rounded-lg transition-colors"
        >
          {t('deforum.picker.checkpoint.installPrompt.installButton')}
        </button>
        <a
          href={`https://civitai.com/models/${item.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="min-h-[44px] min-w-[44px] flex items-center justify-center bg-white/5 hover:bg-white/10 text-white/70 text-xs rounded-lg transition-colors px-2"
        >
          {t('deforum.picker.civitaiModal.viewOnCivitAI')}
        </a>
      </div>
    </div>
  );
}

// ============================================================================
// Install confirmation panel
// ============================================================================

interface InstallConfirmProps {
  item: CivitAISearchItem;
  onConfirm: () => void;
  onCancel: () => void;
}

function InstallConfirm({ item, onConfirm, onCancel }: InstallConfirmProps) {
  const { t } = useTranslation();
  const [accepted, setAccepted] = useState(false);

  return (
    <div className="bg-black/50 border border-white/10 rounded-xl p-4 space-y-3">
      <p className="text-white font-semibold text-sm">
        {t('deforum.picker.checkpoint.installPrompt.title', { name: item.name })}
      </p>
      <p className="text-white/60 text-xs">
        {t('deforum.picker.checkpoint.installPrompt.size', {
          sizeMb: String(item.size_mb),
        })}
      </p>
      <p className="text-white/60 text-xs">
        {t('deforum.picker.checkpoint.installPrompt.license', {
          license: item.license_label,
        })}
      </p>
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
          className="w-4 h-4 accent-purple-500"
        />
        <span className="text-white/70 text-xs">
          {t('deforum.picker.checkpoint.installPrompt.acceptLicense')}
        </span>
      </label>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onConfirm}
          disabled={!accepted}
          className="flex-1 min-h-[44px] bg-purple-600/80 hover:bg-purple-500/90 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
        >
          {t('deforum.picker.checkpoint.installPrompt.installButton')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 min-h-[44px] bg-white/5 hover:bg-white/10 text-white/70 text-sm rounded-lg transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Main modal
// ============================================================================

export function BrowseCivitAICheckpointsModal({
  isOpen,
  onClose,
  onInstalled,
}: BrowseCivitAICheckpointsModalProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [showSdxl, setShowSdxl] = useState(false);
  const [results, setResults] = useState<CivitAISearchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [installState, setInstallState] = useState<InstallState>({ phase: 'idle' });
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const debouncedQuery = useDebounce(query, 400);

  // Clear poll on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current !== null) clearInterval(pollRef.current);
    };
  }, []);

  // Search when query or filter changes
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setSearchError(null);

    searchCivitAICheckpoints({
      query: debouncedQuery || undefined,
      base_model: showSdxl ? 'SDXL 1.0' : 'SD 1.5',
    })
      .then((resp) => {
        if (!cancelled) setResults(resp.items);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setSearchError(err instanceof Error ? err.message : 'Search failed');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, debouncedQuery, showSdxl]);

  const startInstall = useCallback((item: CivitAISearchItem) => {
    setInstallState({ phase: 'confirm', item });
  }, []);

  const confirmInstall = useCallback(async () => {
    if (installState.phase !== 'confirm') return;
    const { item } = installState;

    try {
      const { job_id } = await installCheckpoint({ civitai_version_id: item.latest_version_id });
      const initial: InstallJobStatus = {
        status: 'queued',
        progress_mb: 0,
        total_mb: item.size_mb,
        error: null,
      };
      setInstallState({ phase: 'installing', item, jobId: job_id, status: initial });

      // Poll for completion
      if (pollRef.current !== null) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const status = await getCheckpointInstallStatus(job_id);
          setInstallState((prev) => {
            if (prev.phase !== 'installing') return prev;
            return { ...prev, status };
          });

          if (status.status === 'installed') {
            if (pollRef.current !== null) clearInterval(pollRef.current);
            // Derive filename from item name as best-effort; backend returns actual name
            const filename = `${item.name.toLowerCase().replace(/\s+/g, '_')}.safetensors`;
            setInstallState({ phase: 'done', filename });
            const bm = deriveBaseModel(item.base_model);
            const isHyper = deriveIsHyper(item.base_model);
            onInstalled(filename, bm, isHyper);
          } else if (status.status === 'failed') {
            if (pollRef.current !== null) clearInterval(pollRef.current);
            setInstallState({
              phase: 'error',
              item,
              message: status.error ?? 'Install failed',
            });
          }
        } catch (err: unknown) {
          if (pollRef.current !== null) clearInterval(pollRef.current);
          setInstallState({
            phase: 'error',
            item,
            message: err instanceof Error ? err.message : 'Install failed',
          });
        }
      }, 2000);
    } catch (err: unknown) {
      setInstallState({
        phase: 'error',
        item,
        message: err instanceof Error ? err.message : 'Install failed',
      });
    }
  }, [installState, onInstalled]);

  const cancelInstall = useCallback(() => {
    setInstallState({ phase: 'idle' });
  }, []);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-black/80 border border-white/10 rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 flex-shrink-0">
          <h2 className="text-white font-semibold text-base sm:text-lg">
            {t('deforum.picker.civitaiModal.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-white/60 hover:text-white transition-colors rounded-lg hover:bg-white/10"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Search bar + SDXL toggle */}
        <div className="flex flex-col sm:flex-row gap-2 p-4 border-b border-white/10 flex-shrink-0">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('deforum.picker.civitaiModal.searchPlaceholder')}
            className="input flex-1 bg-black/20 text-white border-white/10 min-h-[44px] placeholder-white/30"
          />
          <label className="flex items-center gap-2 min-h-[44px] px-3 bg-black/20 border border-white/10 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={showSdxl}
              onChange={(e) => setShowSdxl(e.target.checked)}
              className="w-4 h-4 accent-purple-500"
            />
            <span className="text-white/70 text-sm whitespace-nowrap">Show SDXL</span>
          </label>
        </div>

        {/* Install panel (confirmation / progress / error) */}
        {installState.phase !== 'idle' && installState.phase !== 'done' && (
          <div className="p-4 border-b border-white/10 flex-shrink-0">
            {installState.phase === 'confirm' && (
              <InstallConfirm
                item={installState.item}
                onConfirm={confirmInstall}
                onCancel={cancelInstall}
              />
            )}
            {installState.phase === 'installing' && (
              <div className="bg-black/40 border border-white/10 rounded-xl p-4 space-y-2">
                <p className="text-white/70 text-sm">
                  {t('deforum.picker.checkpoint.installProgress', {
                    progress: String(Math.round(installState.status.progress_mb)),
                    total: String(Math.round(installState.status.total_mb)),
                  })}
                </p>
                <div className="w-full bg-white/10 rounded-full h-2">
                  <div
                    className="bg-purple-500 h-2 rounded-full transition-all"
                    style={{
                      width:
                        installState.status.total_mb > 0
                          ? `${Math.min(100, (installState.status.progress_mb / installState.status.total_mb) * 100)}%`
                          : '0%',
                    }}
                  />
                </div>
              </div>
            )}
            {installState.phase === 'error' && (
              <div className="bg-red-900/30 border border-red-500/30 rounded-xl p-4 space-y-2">
                <p className="text-red-400 text-sm">
                  {t('deforum.picker.checkpoint.installFailed', {
                    error: installState.message,
                  })}
                </p>
                <button
                  type="button"
                  onClick={cancelInstall}
                  className="text-white/60 hover:text-white text-xs underline"
                >
                  Dismiss
                </button>
              </div>
            )}
          </div>
        )}

        {installState.phase === 'done' && (
          <div className="p-4 border-b border-white/10 flex-shrink-0">
            <div className="bg-green-900/30 border border-green-500/30 rounded-xl p-3">
              <p className="text-green-400 text-sm">
                {t('deforum.picker.checkpoint.installSuccess')}
              </p>
            </div>
          </div>
        )}

        {/* Results grid */}
        <div className="overflow-y-auto flex-1 p-4">
          {loading && (
            <div className="flex items-center justify-center h-32 text-white/50 text-sm">
              Searching...
            </div>
          )}
          {!loading && searchError && (
            <div className="text-red-400 text-sm text-center py-8">{searchError}</div>
          )}
          {!loading && !searchError && results.length === 0 && (
            <div className="text-white/40 text-sm text-center py-8">
              {t('deforum.picker.civitaiModal.noResults')}
            </div>
          )}
          {!loading && !searchError && results.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {results.map((item) => (
                <ResultCard key={item.id} item={item} onInstall={startInstall} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
